import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $createTableRowNode,
  $getNodeTriplet,
  $isTableRowNode,
  $isTableSelection,
} from "@lexical/table";
import { $createParagraphNode, $createTextNode, $getSelection, $isRangeSelection } from "lexical";
import { useCallback } from "react";
import { getTableColumnWidths } from "../utils/get-table-column-widths";
import { $insertNode } from "../utils/insert-node";
import { $isInTableNode } from "../utils/is-in-table-node";

import type { TableCellNode, TableNode, TableRowNode } from "@lexical/table";

interface CreateTableNodeConfig {
  columns: number;
  rows: number;
}
interface CreateTableNodeFunctions {
  createCell: () => TableCellNode;
  createRow: () => TableRowNode;
  createTable: () => TableNode;
}

export const useLexicalTable = (): {
  createTableNode: (
    config: CreateTableNodeConfig,
    creators: CreateTableNodeFunctions,
  ) => TableNode | undefined;
  insertColumn: (
    insertAfter: boolean,
    { createCell }: Pick<CreateTableNodeFunctions, "createCell">,
  ) => void;
  insertRow: (
    position: "above" | "below" | "top" | "bottom",
    { createCell }: Pick<CreateTableNodeFunctions, "createCell">,
  ) => void;
} => {
  const [editor] = useLexicalComposerContext();

  const createTableNode = useCallback(
    (
      { columns, rows }: CreateTableNodeConfig,
      { createCell, createRow, createTable }: CreateTableNodeFunctions,
    ): TableNode | undefined => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection)) {
        return undefined;
      }
      // Block adding a table to a table
      if ($isInTableNode(selection.anchor.getNode())) {
        return undefined;
      }

      const tableNode = createTable();
      for (let i = 0; i < rows; i += 1) {
        const tableRowNode = createRow();
        for (let j = 0; j < columns; j += 1) {
          const tableCellNode = createCell();
          const paragraphNode = $createParagraphNode();
          tableCellNode.append(paragraphNode);
          tableRowNode.append(tableCellNode);
        }
        tableNode.append(tableRowNode);
      }

      $insertNode(tableNode);
      return tableNode;
    },
    [],
  );

  const insertColumn = useCallback(
    (insertAfter: boolean, { createCell }: Pick<CreateTableNodeFunctions, "createCell">) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!($isRangeSelection(selection) || $isTableSelection(selection))) {
          return false;
        }
        const anchorNode = selection.anchor.getNode();
        // Get a ref to our interested bits
        const [currentSelectedTableCell, , tableNode] = $getNodeTriplet(anchorNode);

        // Get the column index of the selected cell
        const selectedColumnIndex = currentSelectedTableCell.getPreviousSiblings().length;

        // Get all rows from the table
        const rows = tableNode.getChildren().filter($isTableRowNode);

        // Add a new cell to each row at the same column index
        rows.forEach((row) => {
          const cells = row.getChildren();
          const targetCell = cells[selectedColumnIndex];

          if (targetCell) {
            // Create our new table cell node
            const newTableCellNode = createCell().append(
              $createParagraphNode().append($createTextNode()),
            );

            // add it to the row
            if (insertAfter) {
              targetCell.insertAfter(newTableCellNode);
            } else {
              targetCell.insertBefore(newTableCellNode);
            }
          }
        });

        // Rejig the table column widths so that when the new column is added
        // Set all the width on the table so that the columns add up to 100%
        // Columns widths on the table do not update automatically when a column is added
        const previousColumnWidths = getTableColumnWidths(tableNode);

        // splice in the new column width which is the same as the selected column
        const newColumnWidth = previousColumnWidths[selectedColumnIndex]!;
        const newColumnWidths = [...previousColumnWidths];
        newColumnWidths.splice(selectedColumnIndex, 0, newColumnWidth);
        const totalWidth = newColumnWidths.reduce((acc, width) => acc + width);
        const newWidths = newColumnWidths.map((width) => (width / totalWidth) * 100);

        tableNode.setColWidths(newWidths);
      });
    },
    [editor],
  );

  const insertRow = useCallback(
    (
      position: "above" | "below" | "top" | "bottom",
      { createCell }: Pick<CreateTableNodeFunctions, "createCell">,
    ) => {
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) && !$isTableSelection(selection)) {
          return false;
        }
        const anchorNode = selection.anchor.getNode();
        const [, currentRowNode, tableNode] = $getNodeTriplet(anchorNode);

        // Create a new row with the correct number of cells
        const columnCount = tableNode.getColumnCount();
        const tableRowNode = $createTableRowNode();
        for (let i = 0; i < columnCount; i += 1) {
          const tableCellNode = createCell().append($createParagraphNode());
          tableRowNode.append(tableCellNode);
        }

        if (position === "below") {
          currentRowNode.insertAfter(tableRowNode);
        } else if (position === "above") {
          currentRowNode.insertBefore(tableRowNode);
        } else if (position === "top") {
          const [firstRow] = tableNode.getChildren();
          if (firstRow) {
            firstRow.insertBefore(tableRowNode);
          }
        } else if (position === "bottom") {
          const lastRow = tableNode.getChildren()[tableNode.getChildren().length - 1];
          if (lastRow) {
            lastRow.insertAfter(tableRowNode);
          }
        }
      });
    },
    [editor],
  );

  return {
    createTableNode,
    insertColumn,
    insertRow,
  };
};
